// Favourites (saved on this device for 90 days), quick add to cart, and "Picked for you"
// recommendations built from favourites + recently viewed products via Shopify's
// related product recommendations.
const KEY = 'rosedrick:favourites';
const RV_KEY = 'rosedrick:recently-viewed';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_ITEMS = 40;
const root = window.Shopify?.routes?.root || '/';
const HEART = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 20 20\" fill=\"none\"><path stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"var(--icon-stroke-width, 1.5)\" d=\"M10 16.5s-6.25-3.6-6.25-8.1A3.4 3.4 0 0 1 7.2 5c1.15 0 2.2.6 2.8 1.55A3.33 3.33 0 0 1 12.8 5a3.4 3.4 0 0 1 3.45 3.4c0 4.5-6.25 8.1-6.25 8.1Z\"/></svg>";

const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function write(items) {
  try { localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX_ITEMS))); } catch { /* storage unavailable */ }
  window.dispatchEvent(new CustomEvent('favourites:change'));
}

export function readFavourites() {
  try {
    const items = JSON.parse(localStorage.getItem(KEY) || '[]');
    if (!Array.isArray(items)) return [];
    const now = Date.now();
    const fresh = items.filter((item) => item && item.handle && now - (Number(item.savedAt) || 0) < TTL_MS);
    if (fresh.length !== items.length) {
      try { localStorage.setItem(KEY, JSON.stringify(fresh)); } catch { /* ignore */ }
    }
    return fresh;
  } catch {
    return [];
  }
}

function formatMoney(cents, format) {
  const value = (Number(cents) || 0) / 100;
  const group = (num, decimals, thousands, decimal) => {
    const [whole, frac] = num.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? grouped + decimal + frac : grouped;
  };
  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return group(value, 0, ',', '.');
      case 'amount_with_comma_separator': return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
      case 'amount_with_space_separator': return group(value, 2, ' ', ',');
      default: return group(value, 2, ',', '.');
    }
  });
}

export async function addToCart(lines) {
  if (window.Shopify?.actions?.updateCart) {
    const result = await window.Shopify.actions.updateCart({ lines });
    const message = result?.userErrors?.[0]?.message;
    if (message) throw new Error(message);
    document.getElementById('favourites-drawer')?.close?.();
    await window.Shopify.actions.openCart?.();
    return;
  }
  const items = lines.map((line) => ({ id: Number(line.merchandiseId), quantity: line.quantity }));
  const response = await fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.description || '');
  }
  window.location.href = root + 'cart';
}

function toggleMarkup(data, labels) {
  return '<s-fav-toggle class="picked-card__fav" data-fav="' + esc(JSON.stringify(data)) + '" data-label-add="' + esc(labels.add) + '" data-label-saved="' + esc(labels.saved) + '">'
    + '<button type="button" class="fav-icon-btn" aria-pressed="false" aria-label="' + esc(labels.add) + '"><span class="fav-heart" aria-hidden="true">' + HEART + '</span></button>'
    + '</s-fav-toggle>';
}

class FavToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    try { this.data = JSON.parse(this.dataset.fav || '{}'); } catch { this.data = {}; }
    this.onChange = () => this.sync();
    this.onClick = (event) => { event.preventDefault(); event.stopPropagation(); this.toggle(); };
    window.addEventListener('favourites:change', this.onChange);
    window.addEventListener('storage', this.onChange);
    this.button?.addEventListener('click', this.onClick);
    this.sync();
  }

  disconnectedCallback() {
    window.removeEventListener('favourites:change', this.onChange);
    window.removeEventListener('storage', this.onChange);
    this.button?.removeEventListener('click', this.onClick);
  }

  toggle() {
    if (!this.data.handle) return;
    const items = readFavourites();
    const index = items.findIndex((item) => item.handle === this.data.handle);
    if (index > -1) items.splice(index, 1);
    else items.unshift({ ...this.data, savedAt: Date.now() });
    write(items);
  }

  sync() {
    if (!this.button) return;
    const saved = readFavourites().some((item) => item.handle === this.data.handle);
    this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    const label = this.querySelector('[data-fav-label]');
    if (label) label.textContent = saved ? this.dataset.labelSaved : this.dataset.labelAdd;
  }
}

class FavCount extends HTMLElement {
  connectedCallback() {
    this.last = null;
    this.onChange = () => this.update();
    window.addEventListener('favourites:change', this.onChange);
    window.addEventListener('storage', this.onChange);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('favourites:change', this.onChange);
    window.removeEventListener('storage', this.onChange);
  }

  update() {
    const count = readFavourites().length;
    const badge = this.querySelector('[data-fav-badge]');
    if (badge) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
      if (this.last !== null && count > this.last) {
        badge.classList.remove('fav-badge-bump');
        void badge.offsetWidth;
        badge.classList.add('fav-badge-bump');
      }
    }
    const button = this.querySelector('button');
    if (button && this.dataset.label) button.setAttribute('aria-label', this.dataset.label.replace('__count__', String(count)));
    this.last = count;
  }
}

class FavList extends HTMLElement {
  connectedCallback() {
    this.onChange = () => this.render();
    window.addEventListener('favourites:change', this.onChange);
    window.addEventListener('storage', this.onChange);
    this.addEventListener('click', (event) => this.handleClick(event));
    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener('favourites:change', this.onChange);
    window.removeEventListener('storage', this.onChange);
  }

  render() {
    const d = this.dataset;
    const items = readFavourites();
    const list = this.querySelector('[data-fav-items]');
    const empty = this.querySelector('[data-fav-empty]');
    const addAll = this.querySelector('[data-fav-add-all]');
    if (!list) return;
    empty.hidden = items.length > 0;
    const singles = items.filter((item) => item.single && item.variantId);
    if (addAll) addAll.hidden = singles.length < 2;
    list.innerHTML = items.map((item) => {
      const action = item.single && item.variantId
        ? '<button type="button" class="button-primary fav-btn" data-fav-add="' + esc(item.variantId) + '">' + esc(d.labelAdd) + '</button>'
        : (item.available === false
          ? '<span class="fav-item__price">' + esc(d.labelSoldOut) + '</span>'
          : '<a class="button-primary fav-btn" href="' + esc(item.url) + '">' + esc(item.chooseLabel || d.labelChoose) + '</a>');
      const guide = item.guideUrl
        ? '<a class="fav-item__guide" href="' + esc(item.guideUrl) + '">' + esc((d.labelGuide || '').replace('__title__', item.guideTitle || '')) + '</a>'
        : '';
      const image = item.image ? '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" loading="lazy" width="160" height="160">' : '';
      return '<li class="fav-item">'
        + '<a class="fav-item__media" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">' + image + '</a>'
        + '<div class="fav-item__body">'
        + '<a class="fav-item__title" href="' + esc(item.url) + '">' + esc(item.title) + '</a>'
        + '<span class="fav-item__price">' + esc(item.price) + '</span>'
        + guide
        + '<div class="fav-item__actions">' + action
        + '<button type="button" class="fav-link" data-fav-remove="' + esc(item.handle) + '">' + esc(d.labelRemove) + '<span class="sr-only">: ' + esc(item.title) + '</span></button>'
        + '</div><p class="fav-error" data-fav-error hidden></p></div></li>';
    }).join('');
  }

  async handleClick(event) {
    const remove = event.target.closest('[data-fav-remove]');
    if (remove) {
      write(readFavourites().filter((item) => item.handle !== remove.dataset.favRemove));
      return;
    }
    const add = event.target.closest('[data-fav-add]');
    const addAll = event.target.closest('[data-fav-add-all]');
    if (!add && !addAll) return;
    const button = add || addAll;
    if (button.getAttribute('aria-busy') === 'true') return;
    const lines = add
      ? [{ merchandiseId: add.dataset.favAdd, quantity: 1 }]
      : readFavourites().filter((item) => item.single && item.variantId).map((item) => ({ merchandiseId: String(item.variantId), quantity: 1 }));
    if (!lines.length) return;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('opacity-50');
    const errorEl = add ? add.closest('.fav-item')?.querySelector('[data-fav-error]') : this.querySelector('[data-fav-all-error]');
    if (errorEl) errorEl.hidden = true;
    try {
      await addToCart(lines);
    } catch (error) {
      if (errorEl) { errorEl.textContent = error?.message || this.dataset.labelError || ''; errorEl.hidden = false; }
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('opacity-50');
    }
  }
}

class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', async (event) => {
      event.preventDefault();
      if (this.button.getAttribute('aria-busy') === 'true') return;
      const errorEl = this.querySelector('[data-quick-add-error]');
      if (errorEl) errorEl.hidden = true;
      this.button.setAttribute('aria-busy', 'true');
      this.button.classList.add('opacity-50');
      try {
        await addToCart([{ merchandiseId: String(this.dataset.variantId), quantity: 1 }]);
      } catch (error) {
        if (errorEl) { errorEl.textContent = error?.message || this.dataset.errorMessage || ''; errorEl.hidden = false; }
      } finally {
        this.button.removeAttribute('aria-busy');
        this.button.classList.remove('opacity-50');
      }
    });
  }
}

const idCache = new Map();
async function productIdFor(handle) {
  if (idCache.has(handle)) return idCache.get(handle);
  const promise = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => data?.id || null)
    .catch(() => null);
  idCache.set(handle, promise);
  return promise;
}

class PickedForYou extends HTMLElement {
  connectedCallback() {
    this.revision = 0;
    this.onChange = () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.load(), 300); };
    window.addEventListener('favourites:change', this.onChange);
    this.load();
  }

  disconnectedCallback() {
    window.removeEventListener('favourites:change', this.onChange);
    clearTimeout(this.timer);
  }

  async load() {
    const revision = ++this.revision;
    const d = this.dataset;
    const limit = Number(d.limit) || 4;
    const favourites = readFavourites();
    let viewed = [];
    try { viewed = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch { viewed = []; }
    if (!Array.isArray(viewed)) viewed = [];

    const seeds = [];
    const seen = new Set();
    for (const item of [...favourites, ...viewed]) {
      if (!item?.handle || seen.has(item.handle)) continue;
      seen.add(item.handle);
      seeds.push(item);
    }
    const exclude = new Set([d.exclude, ...seen].filter(Boolean));
    const seedList = seeds.slice(0, 3);
    if (!seedList.length) { this.hidden = true; return; }

    const groups = await Promise.all(seedList.map(async (seed) => {
      const id = seed.id || (await productIdFor(seed.handle));
      if (!id) return [];
      try {
        const response = await fetch(root + 'recommendations/products.json?product_id=' + id + '&limit=8&intent=related', { headers: { Accept: 'application/json' } });
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data?.products) ? data.products : [];
      } catch {
        return [];
      }
    }));
    if (revision !== this.revision) return;

    const picks = [];
    const picked = new Set();
    const maxLength = Math.max(...groups.map((group) => group.length), 0);
    for (let i = 0; i < maxLength && picks.length < limit; i += 1) {
      for (const group of groups) {
        const product = group[i];
        if (!product || !product.available || exclude.has(product.handle) || picked.has(product.handle)) continue;
        picked.add(product.handle);
        picks.push(product);
        if (picks.length >= limit) break;
      }
    }

    const list = this.querySelector('[data-picked-list]');
    if (!picks.length || !list) { this.hidden = true; return; }

    const labels = { add: d.labelAdd, saved: d.labelSaved };
    list.innerHTML = picks.map((product) => {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find((v) => v.available) || variants[0] || {};
      const varies = product.price_varies || (product.price_min !== product.price_max);
      const priceText = formatMoney(varies ? product.price_min : (variant.price ?? product.price), d.moneyFormat);
      const price = varies ? (d.labelFrom || '__price__').replace('__price__', priceText) : priceText;
      let image = product.featured_image || '';
      if (image.startsWith('//')) image = 'https:' + image;
      const sized = image ? image + (image.includes('?') ? '&' : '?') + 'width=480' : '';
      const single = variants.length === 1 && Boolean(variant.available);
      const hasColour = (product.options || []).some((option) => /colou?r/i.test(option?.name || option || ''));
      const data = {
        id: product.id,
        handle: product.handle,
        title: product.title,
        url: product.url || (root + 'products/' + product.handle),
        image: sized,
        price,
        variantId: variant.id,
        single,
        available: Boolean(product.available),
        chooseLabel: hasColour ? d.labelChooseColour : '',
      };
      return '<li class="picked-card">'
        + '<a class="picked-card__link" href="' + esc(data.url) + '">'
        + '<span class="picked-card__media">' + (sized ? '<img src="' + esc(sized) + '" alt="' + esc(product.title) + '" loading="lazy" width="480" height="480">' : '') + '</span>'
        + '<span class="picked-card__title">' + esc(product.title) + '</span>'
        + '<span class="picked-card__price">' + esc(price) + '</span>'
        + '</a>' + toggleMarkup(data, labels) + '</li>';
    }).join('');
    this.hidden = false;
  }
}

if (!customElements.get('s-fav-toggle')) customElements.define('s-fav-toggle', FavToggle);
if (!customElements.get('s-fav-count')) customElements.define('s-fav-count', FavCount);
if (!customElements.get('s-fav-list')) customElements.define('s-fav-list', FavList);
if (!customElements.get('s-quick-add')) customElements.define('s-quick-add', QuickAdd);
if (!customElements.get('s-picked-for-you')) customElements.define('s-picked-for-you', PickedForYou);
